"use client";

import type { ReactNode } from "react";
import { cn } from "../../lib";
import {
	SQ_BADGE_LABEL_CLASS,
	sqBadgeGradientClass,
	sqSoftBadgeClass,
} from "../../lib/sq-color-visual-classes";
import type { SqColorName, SqSoftColorName } from "../../lib/sq-colors";

export type SqStatBadgeColorVariant = SqColorName;
export type SqStatBadgeSoftVariant = SqSoftColorName;
export type SqStatBadgeVariant =
	| "neutral"
	| SqStatBadgeColorVariant
	| SqStatBadgeSoftVariant;

/**
 * Default chip height (`h-5`).
 * `compact` matches entity-viewer battle pills (POWER / PWR / LVL): `px-1.5 py-0.5 text-[7px]`.
 */
export type SqStatBadgeSize = "default" | "compact";

export type SqStatBadgeFontWeight = "black" | "semibold" | "medium";

export type SqStatBadgeBorderRadius =
	| "sm"
	| "md"
	| "full"
	| "bottom-full"
	| "none";

/** Neutral pill chrome — shared by learn duration/stop badges and entity tiles. */
export const SQ_STAT_BADGE_NEUTRAL_BASE_CLASS =
	"inline-flex items-center gap-1 rounded-sm border border-border bg-background font-black leading-none uppercase text-foreground";

/** @deprecated Use {@link SQ_STAT_BADGE_NEUTRAL_BASE_CLASS} + size classes via `SqStatBadge`. */
export const SQ_STAT_BADGE_NEUTRAL_SHELL_CLASS = cn(
	SQ_STAT_BADGE_NEUTRAL_BASE_CLASS,
	"h-5 min-h-5 px-2 text-[10px] tracking-[1px]",
);

const SQ_STAT_BADGE_NEUTRAL_SIZE_CLASS: Record<SqStatBadgeSize, string> = {
	default: "h-5 min-h-5 px-2 text-[10px] tracking-[1px]",
	compact: "h-4 min-h-4 px-1.5 text-[9px] tracking-[0.08em]",
};

/** Named container for structure-editor badge sizing. */
export const SQ_STAT_BADGE_ENTITY_STRUCTURE_CONTAINER = "entity-structure";

/** Min inline size (px) on `entity-structure` before default badge size. */
export const ENTITY_STRUCTURE_BADGE_DEFAULT_MIN_PX = 500;

/** Default badge size; compact only below 500px on `entity-structure` container. */
export const SQ_STAT_BADGE_NEUTRAL_RESPONSIVE_SIZE_CLASS = cn(
	SQ_STAT_BADGE_NEUTRAL_SIZE_CLASS.default,
	"@max-[799px]/entity-structure:h-4",
	"@max-[799px]/entity-structure:min-h-4",
	"@max-[799px]/entity-structure:px-1.5",
	"@max-[799px]/entity-structure:text-[9px]",
	"@max-[799px]/entity-structure:tracking-[0.08em]",
);

/** Optical centering nudge for neutral badge text labels. */
export const SQ_STAT_BADGE_LABEL_NUDGE_CLASS = "relative top-[0.5px]";

/** White count pill used inside gradient badges (e.g. unpublished changes). */
export const SQ_STAT_BADGE_NUMBERED_PILL_CLASS =
	"inline-flex h-3 min-w-3 items-center justify-center rounded-full bg-white px-1 text-[10px] font-black leading-none text-amber-950";

const SQ_STAT_BADGE_SIZE_CLASS: Record<SqStatBadgeSize, string> = {
	default: "h-5 min-h-5 px-2 text-[10px]",
	compact: "px-1.5 py-0.5 text-[7px] leading-tight",
};

/** Default badge size; compact only below 500px on `entity-structure` container. */
export const SQ_STAT_BADGE_NON_NEUTRAL_RESPONSIVE_SIZE_CLASS = cn(
	SQ_STAT_BADGE_SIZE_CLASS.default,
	"@max-[799px]/entity-structure:px-1.5",
	"@max-[799px]/entity-structure:py-0.5",
	"@max-[799px]/entity-structure:text-[7px]",
	"@max-[799px]/entity-structure:leading-tight",
	"@max-[799px]/entity-structure:h-auto",
	"@max-[799px]/entity-structure:min-h-0",
);

const SQ_STAT_BADGE_FONT_WEIGHT_CLASS: Record<SqStatBadgeFontWeight, string> = {
	black: "font-black",
	semibold: "font-semibold",
	medium: "font-medium",
};

const SQ_STAT_BADGE_RADIUS_CLASS: Record<SqStatBadgeBorderRadius, string> = {
	sm: "rounded-sm",
	md: "rounded-md",
	full: "rounded-full",
	"bottom-full": "rounded-b-full",
	none: "rounded-none",
};

function isSoftVariant(
	variant: SqStatBadgeVariant,
): variant is SqStatBadgeSoftVariant {
	return (
		variant === "green_soft" ||
		variant === "orange_soft" ||
		variant === "grey_soft" ||
		variant === "indigo_soft"
	);
}

export interface SqStatBadgeProps {
	variant?: SqStatBadgeVariant;
	size?: SqStatBadgeSize;
	/** Default size; compact below 500px on `entity-structure` container only. */
	responsiveSize?: boolean;
	fontWeight?: SqStatBadgeFontWeight;
	borderRadius?: SqStatBadgeBorderRadius;
	icon?: ReactNode;
	/** Renders a white numbered pill before the label (fits inside default `h-5` badge chrome). */
	numberedCount?: number;
	children: ReactNode;
	className?: string;
	title?: string;
	"aria-hidden"?: boolean;
}

export function SqStatBadge({
	variant = "neutral",
	size = "default",
	responsiveSize = false,
	fontWeight = "black",
	borderRadius = "sm",
	icon,
	numberedCount,
	children,
	className,
	title,
	"aria-hidden": ariaHidden,
}: SqStatBadgeProps) {
	const isNeutral = variant === "neutral";
	const isSoft = !isNeutral && isSoftVariant(variant);
	const neutralSizeClass = responsiveSize
		? SQ_STAT_BADGE_NEUTRAL_RESPONSIVE_SIZE_CLASS
		: SQ_STAT_BADGE_NEUTRAL_SIZE_CLASS[size];
	const nonNeutralSizeClass = responsiveSize
		? SQ_STAT_BADGE_NON_NEUTRAL_RESPONSIVE_SIZE_CLASS
		: SQ_STAT_BADGE_SIZE_CLASS[size];
	const showNumberedPill = numberedCount != null;

	return (
		<span
			className={cn(
				"inline-flex items-center gap-1 leading-none uppercase",
				!isNeutral && nonNeutralSizeClass,
				SQ_STAT_BADGE_FONT_WEIGHT_CLASS[fontWeight],
				SQ_STAT_BADGE_RADIUS_CLASS[borderRadius],
				isNeutral
					? cn(SQ_STAT_BADGE_NEUTRAL_BASE_CLASS, neutralSizeClass)
					: isSoft
						? cn(
								"border-0 normal-case tracking-[0.06em]",
								sqSoftBadgeClass(variant),
							)
						: cn(
								sqBadgeGradientClass(variant),
								SQ_BADGE_LABEL_CLASS,
								SQ_STAT_BADGE_FONT_WEIGHT_CLASS[fontWeight],
							),
				className,
			)}
			title={title}
			aria-hidden={ariaHidden}
		>
			{icon}
			{showNumberedPill ? (
				<span className="inline-flex items-center gap-1">
					<span className={SQ_STAT_BADGE_NUMBERED_PILL_CLASS}>
						{numberedCount}
					</span>
					<span
						className={cn(
							(isNeutral || isSoft) &&
								SQ_STAT_BADGE_LABEL_NUDGE_CLASS,
						)}
					>
						{children}
					</span>
				</span>
			) : (
				<span
					className={cn(
						(isNeutral || isSoft) &&
							SQ_STAT_BADGE_LABEL_NUDGE_CLASS,
					)}
				>
					{children}
				</span>
			)}
		</span>
	);
}
